/**
 * The icon set, inline.
 *
 * No icon library: the bundle has to be self-contained (Caddy serves it from the
 * client's own origin with no CDN in the design), and the set is small enough
 * that a dependency would cost more than it saves. Every glyph is a 24×24
 * stroke path on the same grid, so they sit together at any size.
 *
 * Icons are decorative by default — `aria-hidden` — because every status in this
 * portal is colour **and** icon **and** text (design spec §2). Where an icon is
 * the only content, pass a `title` and it becomes a labelled image instead.
 */

export type IconName =
  | 'alert'
  | 'audit'
  | 'ban'
  | 'book'
  | 'building'
  | 'check'
  | 'checkCircle'
  | 'chevronDown'
  | 'chevronLeft'
  | 'chevronRight'
  | 'clock'
  | 'copy'
  | 'dashboard'
  | 'download'
  | 'draft'
  | 'edit'
  | 'externalLink'
  | 'eye'
  | 'file'
  | 'globe'
  | 'group'
  | 'help'
  | 'info'
  | 'key'
  | 'logout'
  | 'mail'
  | 'menu'
  | 'moon'
  | 'more'
  | 'phone'
  | 'plus'
  | 'qr'
  | 'refresh'
  | 'search'
  | 'sun'
  | 'trash'
  | 'truck'
  | 'upload'
  | 'user'
  | 'users'
  | 'x'

const PATHS: Record<IconName, string> = {
  alert: 'M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z',
  audit: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m-6 7h6m-6 4h4',
  ban: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-6.4-2.6 12.8-12.8',
  // The address book (#159): a bound book, its pages under the cover, two lines on the cover.
  book: 'M5 19V5a2 2 0 0 1 2-2h12v14H7a2 2 0 0 0-2 2Zm0 0a2 2 0 0 0 2 2h12v-4M9 7h6M9 11h4',
  building: 'M4 21V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15M14 21V11h4a2 2 0 0 1 2 2v8M3 21h18M8 8h2M8 12h2M8 16h2',
  check: 'm5 13 4 4L19 7',
  checkCircle: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm-3.5-9.2 2.4 2.4 4.6-4.6',
  chevronDown: 'm6 9 6 6 6-6',
  chevronLeft: 'm15 18-6-6 6-6',
  chevronRight: 'm9 18 6-6-6-6',
  clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13.5V12l3 2',
  copy: 'M9 9V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4M5 9h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z',
  dashboard: 'M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z',
  download: 'M12 3v12m0 0 4-4m-4 4-4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  draft: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5M9 13h6m-6 4h4',
  edit: 'M4 20h4L20 8a2.8 2.8 0 0 0-4-4L4 16v4ZM14.5 5.5l4 4',
  externalLink: 'M14 4h6v6M20 4l-9 9M18 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5',
  eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Zm9.5 2.6a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2Z',
  file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Zm0 0v5h5',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM3.2 9h17.6M3.2 15h17.6M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z',
  group: 'M3 7h8v5H3zM13 7h8v5h-8zM8 15h8v5H8z',
  help: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01',
  info: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-9v4.5M12 7.9h.01',
  key: 'M14.5 9.5a4 4 0 1 1-4.6 4L3 20.5V17h3v-3h3l.9-.9a4 4 0 0 1 4.6-3.6Zm2.2-.2h.01',
  logout: 'M15 17l5-5-5-5M20 12H9M12 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6',
  menu: 'M4 6h16M4 12h16M4 18h16',
  moon: 'M20 14.5A8.5 8.5 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z',
  more: 'M12 5.01V5m0 7.01V12m0 7.01V19',
  // #219: «Enviar por correo».
  mail: 'M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm0 1 8 7 8-7',
  phone: 'M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm3 16.5h2',
  plus: 'M12 5v14M5 12h14',
  qr: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z',
  refresh: 'M20 11a8 8 0 0 0-14.1-4.3M4 13a8 8 0 0 0 14.1 4.3M4 5v4h4M20 19v-4h-4',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14Zm5-2 5 5',
  sun: 'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10ZM12 2v2m0 16v2M4.2 4.2l1.5 1.5m12.6 12.6 1.5 1.5M2 12h2m16 0h2M4.2 19.8l1.5-1.5M18.3 5.7l1.5-1.5',
  trash: 'M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m3 0v12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V7m4 4v6m4-6v6',
  truck: 'M3 16V6a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v10M14 9h3.6a1 1 0 0 1 .8.4l2.4 3.2a1 1 0 0 1 .2.6V16M3 16h1.5m5 0H14m3.5 0H21M7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm12 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  upload: 'M12 16V4m0 0L8 8m4-4 4 4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
  users: 'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 8a7 7 0 0 1 14 0m2-15.7a4 4 0 0 1 0 7.4M18 20a7 7 0 0 0-3-5.7',
  x: 'M6 6l12 12M18 6 6 18',
}

export interface IconProps {
  name: IconName
  size?: number
  /** Present only where the icon carries meaning nothing else on screen does. */
  title?: string
  className?: string
}

export function Icon({ name, size = 18, title, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name]} />
    </svg>
  )
}
